import type { TripPlannerCommittedStateEntry } from 'tripPlanner/assistant/surfaceSnapshots/committedStateEntry'
import { createContext, useContext, type MutableRefObject } from 'react'
import type { MarkerSelectionData } from 'tripPlanner/components/Chat/chatMapConstants'
import type { RightPanelView } from '../hooks/useRightPanelState'
import type { DrawerStackEntry } from '../hooks/useDrawerStackState'

export interface CartItemPrice {
  itemId: string
  // What this customer will actually be charged -- reflects real LuxPlus entitlement,
  // matching the cart panel and navbar cart total, not the preview toggle.
  checkoutPrice?: string
  // Whether this item's price counts toward the total the customer is quoted --
  // matches computeCartPanelTotal's own eligibility check (cartExcluded/includeInTotal)
  // exactly, so the assistant can reconstruct the real total instead of over-counting
  // customer-excluded or not-yet-bookable items.
  countsTowardTotal: boolean
  // Actual checkout eligibility, independent of price precision or availability of a price.
  isBookingReady: boolean
  // A short reason this item can't be checked out yet (e.g. "Dates required"), matching
  // the same cartStatus the cart panel tiles use for their own warning banners. Undefined
  // when no known blocker exists; absence alone does not establish readiness.
  checkoutBlocker?: string
}

// The List row's own LuxPlus preview toggle, which any customer can flip regardless of real
// membership. Contributed only while List is visible, so it is not part of CartItemPrice.
export interface CartDisplayedPrice {
  itemId: string
  displayedPrice?: string
}

export interface CartContextSummary {
  status: 'loading' | 'error' | 'partial' | 'ready'
  estimatedTotal?: string
  readyItemCount: number
}

export interface TurnAuthoritySnapshot {
  cartSummary?: CartContextSummary
  selectedFlightEntries?: ReadonlyMap<string, TripPlannerCommittedStateEntry>
  visibleDrawers?: Map<symbol, { label?: string, centralEntryId?: string }>
  mobileOverlay?: string
  mobileOverlayOwner?: symbol
  rightPanelOwner?: symbol
  drawerOwner?: symbol
  cartOwner?: symbol
  cartDisplayOwner?: symbol
  displayedCartItemPrices?: ReadonlyArray<CartDisplayedPrice>
  rightPanelView: RightPanelView | 'chat' | undefined
  selectedMarkerData: MarkerSelectionData | undefined
  isAnyDrawerOpen: boolean
  topEntry: DrawerStackEntry | undefined
  cartItemPrices: Array<CartItemPrice>
}

export const DEFAULT_TURN_AUTHORITY_SNAPSHOT: TurnAuthoritySnapshot = {
  rightPanelView: undefined,
  selectedMarkerData: undefined,
  isAnyDrawerOpen: false,
  topEntry: undefined,
  cartItemPrices: [],
}

export const TurnAuthoritySnapshotContext = createContext<MutableRefObject<TurnAuthoritySnapshot> | undefined>(undefined)

/**
 * Returns a stable ref (not state) holding the send-time right-panel/drawer snapshot -- read
 * imperatively via `.current`, never during render. `resolveTurnAuthority` reads this at submit time to populate `visibleState`.
 */
export function useTurnAuthoritySnapshot(): MutableRefObject<TurnAuthoritySnapshot> {
  const context = useContext(TurnAuthoritySnapshotContext)
  if (!context) {
    throw new Error('useTurnAuthoritySnapshot must be used within TripChatRuntime')
  }

  return context
}
